import dayjs from 'dayjs';
import type {
  UpdateWebsiteDateRequest,
  WWeddingAccountView,
  WWeddingView,
} from '@zola/svc-web-api-ts-client';
import { dataFormat, inputFormat } from '@zola/zola-ui/src/components/Form/util/dateUtils';
import { UpdateWeddingRequestClientType } from 'actions/WeddingActions';
import type { WebsitePreviewReducerState } from 'reducers/website/websitePreview';
import utc from 'dayjs/plugin/utc';
import { WeddingDateDisplayFormatEnum } from '../../../../../../../util/websiteDateUtil';

dayjs.extend(utc);

export type WebsiteDateFormValues = {
  weddingDateRangeStart: string;
  weddingDateRangeEnd: string;
  customDateMessage: string;
};

export type FormValues = {
  weddingDateDisplayFormat: WeddingDateDisplayFormatEnum;
  wedding: WWeddingView;
  websiteDate: WebsiteDateFormValues;
  weddingDate?: string; // MM/DD/YYYY
  // Needed for wedding date validation
  currentlySelectedDate?: string;
  registryIsPublic?: boolean;
};

const mapWebsiteDateToFormValues = (account: WWeddingAccountView): WebsiteDateFormValues => {
  const { wedding_date_range_start, wedding_date_range_end, custom_date_message } = account;
  return {
    weddingDateRangeStart: wedding_date_range_start
      ? dayjs.utc(wedding_date_range_start).format(inputFormat)
      : '',
    weddingDateRangeEnd: wedding_date_range_end
      ? dayjs.utc(wedding_date_range_end).format(inputFormat)
      : '',
    customDateMessage: custom_date_message || '',
  };
};

type ReduxDataForForm = {
  account: WWeddingAccountView;
  wedding: WWeddingView;
  registryIsPublic?: boolean;
};

export const mapDataToFormValues = (data: ReduxDataForForm): FormValues => {
  const { account, wedding, registryIsPublic } = data;
  // Populate chosen date format dropdown option
  const getDisplayFormat = () => {
    const { wedding_date_range_start, custom_date_message } = account;
    if (wedding_date_range_start) {
      return WeddingDateDisplayFormatEnum.RANGE;
    }
    if (custom_date_message) {
      return WeddingDateDisplayFormatEnum.CUSTOM;
    }
    return WeddingDateDisplayFormatEnum.EXACT;
  };

  const { wedding_date } = wedding;

  const initialValues: FormValues = {
    wedding,
    weddingDate: wedding_date ? dayjs.utc(wedding_date).format(inputFormat) : undefined,
    weddingDateDisplayFormat: getDisplayFormat(),
    websiteDate: mapWebsiteDateToFormValues(account),
    currentlySelectedDate: wedding_date,
    registryIsPublic,
  };
  return initialValues;
};

export const mapFormValuesToUpdateWebsiteDateRequest = (
  values: FormValues
): UpdateWebsiteDateRequest => {
  const { websiteDate, weddingDateDisplayFormat } = values;
  const { customDateMessage, weddingDateRangeStart, weddingDateRangeEnd } = websiteDate;
  if (weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.RANGE) {
    return {
      date_range_start: dayjs.utc(weddingDateRangeStart, inputFormat).toDate(),
      date_range_end: dayjs.utc(weddingDateRangeEnd, inputFormat).toDate(),
    };
  }
  if (weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.CUSTOM) {
    return {
      custom_date_message: customDateMessage,
    };
  }
  return {
    custom_date_message: undefined,
    date_range_start: undefined,
    date_range_end: undefined,
  };
};

export const mapFormValuesToUpdateWeddingRequest = (
  values: FormValues
): UpdateWeddingRequestClientType | undefined => {
  const { wedding: request, weddingDate } = values;
  // format date
  request.wedding_date = dayjs.utc(weddingDate).format(dataFormat);

  return request;
};

export const mapFormValuesToWebsitePreview = (
  values: FormValues
): Partial<WebsitePreviewReducerState> => {
  const { wedding: weddingFormValues, websiteDate, weddingDateDisplayFormat } = values;
  const previewValues = {
    title: weddingFormValues.title,
    hashtag: weddingFormValues.hashtag,
    city: weddingFormValues.city,
    stateProvince: weddingFormValues.state_province,
    date: dayjs.utc(values.weddingDate).format(dataFormat),
    weddingDateDisplayFormat,
    ...websiteDate,
  };
  return { weddingValues: previewValues };
};
